import { useState, useEffect } from 'react';
import API_CONFIG from '../../config/api';
import './CreateAdminModal.css'; // Reusing modal styles

function DigioSettingsModal({ isOpen, onClose, onSave, adminSession }) {
    const [config, setConfig] = useState({
        clientId: '',
        clientSecret: '',
        templateName: 'SELFIE_KYC',
        environment: 'sandbox',
        baseUrl: 'https://api.digio.in',
        mandateCorporateConfigId: '',
        mandateAuthMode: 'API',
        mandateNotifyCustomer: true,
        mandateIncludeAuthUrl: true,
        mandateExpireInDays: 10,
        mandateCustomerNotificationMode: 'SMS'
    });
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        const fetchSettings = async () => {
            try {
                const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/settings`, {
                    headers: { 'x-user-id': adminSession?.userId }
                });
                const data = await res.json();
                if (data.success) {
                    const s = data.data || {};
                    setConfig(prev => ({
                        ...prev,
                        clientId: s['digio.client_id'] || prev.clientId,
                        clientSecret: s['digio.client_secret'] || prev.clientSecret,
                        templateName: s['digio.template_name'] || prev.templateName,
                        environment: s['digio.environment'] || prev.environment,
                        baseUrl: s['digio.base_url'] || prev.baseUrl,
                        mandateCorporateConfigId: s['digio.mandate.corporate_config_id'] || prev.mandateCorporateConfigId,
                        mandateAuthMode: s['digio.mandate.auth_mode'] || prev.mandateAuthMode,
                        mandateNotifyCustomer: s['digio.mandate.notify_customer'] === 'true' ? true : prev.mandateNotifyCustomer,
                        mandateIncludeAuthUrl: s['digio.mandate.include_authentication_url'] === 'true' ? true : prev.mandateIncludeAuthUrl,
                        mandateExpireInDays: s['digio.mandate.expire_in_days'] ? Number(s['digio.mandate.expire_in_days']) : prev.mandateExpireInDays,
                        mandateCustomerNotificationMode: s['digio.mandate.customer_notification_mode'] || prev.mandateCustomerNotificationMode
                    }));
                }
            } catch (e) {
                console.error('Failed to load Digio settings', e);
            }
        };
        fetchSettings();
    }, [isOpen]);

    const handleChange = (e) => {
        setConfig({
            ...config,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);

        const saveSettings = async () => {
            try {
                const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/settings`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-user-id': adminSession?.userId
                    },
                    body: JSON.stringify({
                        settings: {
                            'digio.client_id': config.clientId,
                            'digio.client_secret': config.clientSecret,
                            'digio.template_name': config.templateName,
                            'digio.environment': config.environment,
                            'digio.base_url': config.baseUrl,
                            'digio.mandate.corporate_config_id': config.mandateCorporateConfigId,
                            'digio.mandate.auth_mode': config.mandateAuthMode,
                            'digio.mandate.notify_customer': String(config.mandateNotifyCustomer),
                            'digio.mandate.include_authentication_url': String(config.mandateIncludeAuthUrl),
                            'digio.mandate.expire_in_days': String(config.mandateExpireInDays),
                            'digio.mandate.customer_notification_mode': config.mandateCustomerNotificationMode
                        }
                    })
                });
                const data = await res.json();
                if (!data.success) throw new Error(data.message || 'Save failed');
                setMessage('✅ Configuration saved successfully!');
                setTimeout(() => {
                    setMessage('');
                    onSave(config);
                    onClose();
                }, 1200);
            } catch (err) {
                setMessage(err.message || 'Failed to save configuration');
            } finally {
                setIsLoading(false);
            }
        };
        saveSettings();
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content create-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>⚙️ Digio KYC Configuration</h2>
                    <button className="modal-close-btn" onClick={onClose}>×</button>
                </div>

                <div className="modal-body" style={{ padding: '20px' }}>
                    <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
                        Configure your Digio API credentials and environment settings here.
                        Keep these credentials secure as they provide access to sensitive KYC data.
                    </p>

                    {message && (
                        <div style={{ padding: '10px', background: '#d1fae5', color: '#065f46', borderRadius: '6px', marginBottom: '15px', textAlign: 'center' }}>
                            {message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="create-form">
                        <div className="form-group">
                            <label>Digio Client ID</label>
                            <input
                                type="text"
                                name="clientId"
                                value={config.clientId}
                                onChange={handleChange}
                                placeholder="e.g. AI0123456789"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Digio Client Secret</label>
                            <input
                                type="password"
                                name="clientSecret"
                                value={config.clientSecret}
                                onChange={handleChange}
                                placeholder="Your Digio Secret Key"
                                required
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Default Template</label>
                                <select name="templateName" value={config.templateName} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}>
                                    <option value="SELFIE_KYC">Selfie KYC</option>
                                    <option value="CKYC">Central KYC</option>
                                    <option value="AADHAR_E_KYC">Aadhar e-KYC</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Environment</label>
                                <select name="environment" value={config.environment} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}>
                                    <option value="sandbox">Sandbox (Testing)</option>
                                    <option value="production">Production (Live)</option>
                                </select>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>API Base URL</label>
                            <input
                                type="text"
                                name="baseUrl"
                                value={config.baseUrl}
                                onChange={handleChange}
                                placeholder="https://api.digio.in"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Mandate Corporate Config ID</label>
                            <input
                                type="text"
                                name="mandateCorporateConfigId"
                                value={config.mandateCorporateConfigId}
                                onChange={handleChange}
                                placeholder="e.g. CORP123456"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Mandate Auth Mode</label>
                                <select name="mandateAuthMode" value={config.mandateAuthMode} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}>
                                    <option value="API">API</option>
                                    <option value="ESIGN">ESIGN</option>
                                    <option value="PHYSICAL">PHYSICAL</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Mandate Expiry (Days)</label>
                                <input
                                    type="number"
                                    name="mandateExpireInDays"
                                    min="1"
                                    max="90"
                                    value={config.mandateExpireInDays}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Mandate Notify Customer</label>
                                <select name="mandateNotifyCustomer" value={config.mandateNotifyCustomer ? 'true' : 'false'} onChange={(e) => setConfig({ ...config, mandateNotifyCustomer: e.target.value === 'true' })} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Mandate Include Auth URL</label>
                                <select name="mandateIncludeAuthUrl" value={config.mandateIncludeAuthUrl ? 'true' : 'false'} onChange={(e) => setConfig({ ...config, mandateIncludeAuthUrl: e.target.value === 'true' })} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Mandate Customer Notification Mode</label>
                                <select name="mandateCustomerNotificationMode" value={config.mandateCustomerNotificationMode} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}>
                                    <option value="SMS">SMS</option>
                                    <option value="WHATSAPP">WhatsApp</option>
                                    <option value="ALL">All</option>
                                </select>
                            </div>
                        </div>

                        <div className="modal-actions" style={{ marginTop: '20px' }}>
                            <button type="button" className="btn btn-cancel" onClick={onClose} disabled={isLoading}>Cancel</button>
                            <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                {isLoading ? 'Saving...' : '💾 Save Configuration'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default DigioSettingsModal;
